import { useEffect, useRef } from 'react';
import { useSessionStore } from '../stores/session/sessionStore';
import { clearSession } from '../stores/session/clearSession';
import { useTenantStore } from '../stores/tenantStore';
import { useThemeStore } from '../stores/themeStore';
import { getAcombagoOrigins } from '../utils/portal/portalConfig';
import { clearRenonceGuard, reloadToPortalUrl, requestPortalRenonce } from '../utils/portal/portalRenonce';

// A failed re-nonce is frequently transient on the host side: the same request, reissued a moment
// later, succeeds. Long enough for whatever was briefly unavailable to come back, short enough that
// the reconnecting overlay still reads as one continuous recovery rather than a stall.
const AUTO_RETRY_DELAY_MS = 2_000;

export function usePortalEvents(): void {
    const setConnectSessionFailed = useSessionStore((s) => s.setConnectSessionFailed);
    const setReconnecting = useSessionStore((s) => s.setReconnecting);
    const clearTenant = useTenantStore((s) => s.clearTenant);
    const setDark = useThemeStore((s) => s.setDark);
    // Whether this recovery has already been reissued automatically. One retry only — past that the
    // failure is not transient, and silently reissuing forever would hide a real outage.
    const autoRetriedRef = useRef(false);
    const autoRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        function cancelAutoRetry(): void {
            if (autoRetryTimerRef.current === null) return;
            clearTimeout(autoRetryTimerRef.current);
            autoRetryTimerRef.current = null;
        }

        // MessageEvent<unknown> rather than the default MessageEvent, whose `data` is `any` — the
        // payload crosses a window boundary, so it is untrusted shape as well as untrusted origin.
        // Each field is still checked at runtime below; the type just stops `any` leaking inward.
        function handleMessage(event: MessageEvent<unknown>): void {
            const origins = getAcombagoOrigins();
            if (origins.length === 0 || !origins.includes(event.origin)) return;

            const data = event.data;
            if (typeof data !== 'object' || data === null) return;
            const message = data as Record<string, unknown>;
            if (typeof message.type !== 'string') return;

            switch (message.type) {
                case 'portal:signout':
                    clearSession();
                    clearTenant();
                    break;
                case 'portal:theme':
                    setDark(message.dark === true);
                    break;
                case 'portal:nonce-refresh':
                    // The host minted a fresh launch nonce after our AcceoConnect session went
                    // cold. Reload into it — useAuthGuard re-runs the portal exchange, re-establishing
                    // the app session and the server-side Connect session.
                    autoRetriedRef.current = false;
                    // The host has answered, so a still-scheduled retry has nothing left to ask for.
                    // Left armed it would post after the reload, against a session already recovered.
                    cancelAutoRetry();
                    if (typeof message.url === 'string') reloadToPortalUrl(message.url);
                    break;
                case 'portal:nonce-refresh-error':
                    if (autoRetryTimerRef.current !== null) {
                        // The retry is scheduled but not yet issued, so no reply to it can have
                        // arrived: this error answers an earlier request. The protocol carries no
                        // request id to tell them apart, so let the pending retry stand rather than
                        // spend it on a failure it never asked for.
                        break;
                    }
                    if (!autoRetriedRef.current) {
                        // First failure of this recovery — reissue it ourselves. The reconnecting
                        // overlay deliberately stays up: the operator did not ask for this recovery,
                        // so they should not have to acknowledge a failure we can retry on our own.
                        autoRetriedRef.current = true;
                        autoRetryTimerRef.current = setTimeout(() => {
                            autoRetryTimerRef.current = null;
                            // Released only now, immediately before reissuing. Releasing it up front
                            // would leave the window open for one of the other callers to post its
                            // own uncorrelated request — and a cold session is normally discovered by
                            // several parallel calls, so that is the common case, not the corner one.
                            clearRenonceGuard();
                            if (requestPortalRenonce()) return;
                            // Nothing trusted left to ask, so no reply can ever arrive. Surface the
                            // failure rather than leaving the spinner up on a dead recovery.
                            setReconnecting(false);
                            setConnectSessionFailed(true);
                        }, AUTO_RETRY_DELAY_MS);
                        break;
                    }
                    // The retry failed too, so the host cannot re-mint a nonce right now. Release the
                    // guard so the overlay's own retry button still reaches the host, then drop the
                    // spinner and surface the actionable "reopen from AcombaGO" message.
                    clearRenonceGuard();
                    setReconnecting(false);
                    setConnectSessionFailed(true);
                    break;
            }
        }

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
            cancelAutoRetry();
        };
    }, [setConnectSessionFailed, setReconnecting, clearTenant, setDark]);
}
